'use client'

import { useEffect } from 'react'
import { Wrench } from 'lucide-react'
import { ErrorScreen } from '@/components/compte/ErrorScreen'

/**
 * Boundary global. Affiche la stack pour qu'on puisse débugger les crashes mobiles.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    console.error('App-level error:', error)
    // ChunkLoadError : arrive après un redéploiement (les anciens fichiers JS
    // n'existent plus). On recharge AUTOMATIQUEMENT pour récupérer la nouvelle
    // version — avec une garde anti-boucle (1 rechargement / 20 s max).
    const txt = `${error?.name || ''} ${error?.message || ''}`
    if (/ChunkLoadError|Loading chunk [\w-]+ failed|Loading CSS chunk|error loading dynamically imported/i.test(txt)) {
      try {
        const k = 'blc.chunkReload'
        const last = Number(sessionStorage.getItem(k) || '0')
        if (Date.now() - last > 20000) {
          sessionStorage.setItem(k, String(Date.now()))
          location.reload()
        }
      } catch { location.reload() }
    }
  }, [error])

  return (
    <ErrorScreen
      title="Oups, quelque chose a planté"
      text="Capture cette page et envoie-la pour qu’on identifie la cause."
      error={error}
      onRetry={() => reset()}
      homeHref="/"
      extra={
        <button
          type="button"
          onClick={() => {
            try {
              // Vide TOUT (localStorage + sessionStorage + service workers + caches)
              // pour repartir d'un état sain en cas de corruption de données locales.
              localStorage.clear()
              sessionStorage.clear()
              if ('serviceWorker' in navigator) {
                navigator.serviceWorker.getRegistrations().then(rs => rs.forEach(r => r.unregister()))
              }
              if ('caches' in window) {
                caches.keys().then(keys => keys.forEach(k => caches.delete(k)))
              }
            } catch {}
            setTimeout(() => { location.href = '/app' }, 300)
          }}
          className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-rose-300 border border-rose-400/30 hover:bg-rose-500/10 transition">
          <Wrench size={16} /> Vider les données locales et recharger
        </button>
      }
    />
  )
}
